const URL = "https://secureimage.securedataimages.com/images/cams";

// Sound URLs used in the app
const SOUND_URLS = {
  memberEnter: `${URL}/sound/viewerWithCash.mp3`,
  memberLeave: `${URL}/sound/viewerLeave.mp3`,
  startPrivate: `${URL}/sound/startPrivate.mp3`,
  endPrivate: `${URL}/sound/endPrivate.mp3`,
  giftReceived: `${URL}/sound/GiftingSound.mp3`,
  greatIdea: `${URL}/sound/GreatIdea.mp3`,
  coinGameWin: `${URL}/sound/CoinGameWin.mp3`,
  fxGameWin2: `${URL}/sound/FXGameWin2.mp3`,
  fxGameWin4: `${URL}/sound/FXGameWin4.mp3`,
  superBuzz: `${URL}/camsviewer/sound/super_buzzclick.mp3`,
  buzz: `${URL}/camsviewer/sound/buzzclick.mp3`,
};

class CamsAudio {
  private audioCache: Map<string, HTMLAudioElement> = new Map();
  private unlocked = false;
  private audioContext: AudioContext | null = null;

  /**
   * Call this on a user gesture (click/tap) to unlock audio playback on Safari iOS.
   * Uses Web Audio API to unlock audio context, then pre-loads audio elements.
   */
  unlock = () => {
    if (this.unlocked) return;
    this.unlocked = true;

    // Create and resume AudioContext to unlock Web Audio on iOS
    // This is the most reliable way to unlock audio on Safari iOS
    try {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        this.audioContext = new AudioContextClass();
        // Create a silent buffer and play it to unlock
        const buffer = this.audioContext.createBuffer(1, 1, 22050);
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);
        source.start(0);
        // Resume context if suspended (iOS Safari)
        if (this.audioContext.state === "suspended") {
          this.audioContext.resume();
        }
      }
    } catch (e) {
      // AudioContext not supported, fall through to HTMLAudioElement approach
    }

    // Pre-load all audio elements (without playing) so they're ready
    Object.values(SOUND_URLS).forEach(url => {
      const audio = new Audio(url);
      audio.preload = "auto";
      audio.load();
      this.audioCache.set(url, audio);
    });
  };

  /**
   * Pre-load additional audio URLs into the cache so they play reliably.
   * Should be called after unlock() or during a user gesture.
   */
  preloadUrls = (urls: string[]) => {
    urls.forEach(url => {
      if (!this.audioCache.has(url)) {
        const audio = new Audio(url);
        audio.preload = "auto";
        audio.load();
        this.audioCache.set(url, audio);
      }
    });
  };

  /**
   * Play any URL through the cache/safe-play mechanism.
   */
  play = (url: string) => {
    this.safePlay(url);
  };

  private safePlay = (url: string) => {
    const cached = this.audioCache.get(url);
    if (cached) {
      cached.currentTime = 0;
      cached.play().catch(() => {});
    } else {
      new Audio(url).play().catch(() => {});
    }
  };

  playMemberEnter = () => {
    this.safePlay(SOUND_URLS.memberEnter);
  };
  playMemberLeave = () => {
    this.safePlay(SOUND_URLS.memberLeave);
  };
  playStartPrivateChat = () => {
    this.safePlay(SOUND_URLS.startPrivate);
  };
  playEndPrivateChat = () => {
    this.safePlay(SOUND_URLS.endPrivate);
  };
  playGiftReceived = () => {
    this.safePlay(SOUND_URLS.giftReceived);
  };
  playGreatIdea = () => {
    this.safePlay(SOUND_URLS.greatIdea);
  };
  playCoinGameWin = () => {
    this.safePlay(SOUND_URLS.coinGameWin);
  };
  playFXGameWin2 = () => {
    this.safePlay(SOUND_URLS.fxGameWin2);
  };
  playFXGameWin4 = () => {
    this.safePlay(SOUND_URLS.fxGameWin4);
  };
  playSuperBuzz = () => {
    this.safePlay(SOUND_URLS.superBuzz);
  };
  playBuzz = () => {
    this.safePlay(SOUND_URLS.buzz);
  };
}

const camsAudio = new CamsAudio();
export default camsAudio;
